import { InputText } from 'primereact/inputtext'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { Button, Col, Container, Row } from 'react-bootstrap'
import AuthContext from '../../../context';
import { Toast } from 'primereact/toast';
import { EsubeApi, Platform } from '../../../../../services/authentication/api';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { login } from '../../../../../stores/auth';
import { Configuration } from '../../../../../services/authentication/configuration';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { WatchListAllSymbolsServiceApi } from '../../../../../services/actionHub/api';
import { APIControl } from '../../../../../helpers/APIControl';
import { setWatchlist } from '../../../../../stores/watchlist';
import { ProfileServiceApi } from '../../../../../services/profile/api';
import { setProfileBasicDetail } from '../../../../../stores/profileSlice';
import { setMainDefaultSecurity, setSelectedSecurity } from '../../../../../stores/security';
import { useTranslation } from 'react-i18next';
import JSEncrypt from 'jsencrypt';

const SMS_RESEND_SECONDS = 180;
const SMS_INFO_TEXT = "Lütfen cep telefonunuza SMS olarak gelen 6 haneli onay kodunu giriniz.";
const MetKey = `-----BEGIN PUBLIC KEY-----
MIIBITANBgkqhkiG9w0BAQEFAAOCAQ4AMIIBCQKCAQB3prLK5rWvXhOfC8JyMImg
NWH1XaY8+pQGs5FVy0dAPcogNi7FJKQvjNOx2nNl4ESU1ShT3TNe69L8x45MR2zg
OGbgN1NXdCCYu7bsJlQUxWuu1jEqZVl5PKgz8JSrkk47kRUFHw2Ogb50nYhR9X0q
LaU+UZshu5UG/ut/gaSS8TStrHwGifYsVRJIytSldEWlUWtuh19dc7RHVhwd3nd5
lBqqJC3cy2va2rRjC0f/UwHCANfvOPMT1Kfe+9ygihQ8l9B199+kgzy1Z/loBRsr
mVttnyKF9qvjzXbk5ZaFznyAFOu0pdLf3Wqp0X7YPFV7K2tqBoLJACUpH2NSUDej
AgMBAAE=
-----END PUBLIC KEY-----`;

export default function SmsVerification() {
    const encrypt = new JSEncrypt();
    encrypt.setPublicKey(MetKey);
    const eSubeApi = new EsubeApi(new Configuration({ basePath: process.env.REACT_APP_AUTHENTICATION_API_BASE_PATH }));
    const watchListAllSymbolsServiceApi = new WatchListAllSymbolsServiceApi(
        new Configuration({ basePath: process.env.REACT_APP_ACTION_HUB_API_BASE_PATH }),
        undefined,
        APIControl(process.env.REACT_APP_ACTION_HUB_API_BASE_PATH)
    );
    const profileService = new ProfileServiceApi(
        new Configuration({ basePath: process.env.REACT_APP_PROFILE_API_BASE_PATH }),
        undefined,
        APIControl(process.env.REACT_APP_PROFILE_API_BASE_PATH)
    );

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const toast = useRef(null);
    const contextVal = useContext(AuthContext);
    const [verificationCode, setVerificationCode] = useState("");
    const [seconds, setSeconds] = useState(SMS_RESEND_SECONDS);
    const [isLoading, setIsLoading] = useState(false);
    const { i18n } = useTranslation();

    useEffect(() => {
        if (seconds <= 0) return undefined;

        const timer = setTimeout(() => {
            setSeconds((prevSeconds) => Math.max(prevSeconds - 1, 0));
        }, 1000);

        return () => clearTimeout(timer);
    }, [seconds]);

    const formatTime = (time) => {
        const minutes = Math.floor(time / 60);
        const remainingSeconds = time % 60;
        return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
    };

    const clickResendCode = () => {
        if (seconds > 0 || isLoading) return;

        try {
            const customerNumber = parseInt(contextVal?.authState.userLoginInputs.customerNumber, 10);
            const encryptedPassword = encrypt.encrypt(contextVal?.authState.userLoginInputs.password);

            if (!customerNumber || Number.isNaN(customerNumber) || !encryptedPassword) {
                toast.current.show({
                    severity: "error",
                    summary: "Hata",
                    detail: "SMS gönderimi için kullanıcı bilgileri bulunamadı.",
                    life: 3000
                });
                return;
            }

            setIsLoading(true);

            eSubeApi.apiEsubeLoginForceSmsPost(Platform.Web, { CustomerNumber: customerNumber, Password: encryptedPassword })
                .then((res) => {
                    if (res.data?.Status === "Error" || !res.data?.Data) {
                        toast.current.show({
                            severity: "error",
                            summary: "Hata",
                            detail: res.data?.Error || "SMS gönderilemedi. Lütfen tekrar deneyiniz.",
                            life: 3000
                        });
                        return;
                    }

                    contextVal.setAuthState({
                        ...contextVal.authState,
                        userLoginInputs: {
                            ...contextVal.authState.userLoginInputs,
                            mfaTraceId: res.data.Data.MFATraceId || "",
                        },
                    });
                    setVerificationCode("");
                    setSeconds(SMS_RESEND_SECONDS);
                    toast.current.show({
                        severity: "success",
                        summary: "Başarılı",
                        detail: "SMS doğrulama kodu tekrar gönderildi.",
                        life: 3000
                    });
                })
                .catch((error) => {
                    console.error("clickResendCode error:", error);
                    toast.current.show({
                        severity: "error",
                        summary: "Hata",
                        detail: error?.response?.data?.Error || error?.message || "Kod gönderme işlemi başarısız",
                        life: 3000
                    });
                })
                .finally(() => {
                    setIsLoading(false);
                });
        } catch (error) {
            setIsLoading(false);
            toast.current.show({
                severity: "error",
                summary: "Hata",
                detail: "Kod gönderme işlemi başarısız",
                life: 3000
            });
            console.error("clickResendCode error:", error);
        }
    };

    // Helper functions for post-login operations
    const getUserWatchlistData = (token) => {
        try {
            const culture = "tr-TR";
            watchListAllSymbolsServiceApi.actionhubWatchlistallsymbolsGet(token, culture).then((response) => {
                if (!response.data.error) {
                    console.log("getUserWatchlistData:", response.data.watchlist);
                    let data = response.data.watchlist;
            
                    data = data.filter(element => !element.id.includes('xu030') && !element.id.includes('xu100'));
            
                    const favoritesSecuritiesData = data.flatMap(element =>
                        element.securitiesEntityIds.map(security => ({
                            ...security,
                            watchlistId: element.id,
                            watchlistName: element.name,
                        }))
                    );
            
                    dispatch(setWatchlist(favoritesSecuritiesData));
                } else {
                    console.error("getUserWatchlistData:", response.data.error);
                }
            });
        } catch (error) {
            console.error("getUserWatchlistData error:", error);
        }
    };

    const getProfileBasicDetail = async (token) => {
        try {
            await profileService.profileBasicprofiledetailGet(token).then((response) => {
                console.log('getProfileBasicDetail', response);
                if (response.data.error != "") {
                    console.log('getProfileBasicDetail', response.data.error);
                    toast.current.show({ severity: 'error', summary: 'Hata', detail: response.data.error, life: 3000 });
                    return;
                }
                
                dispatch(setProfileBasicDetail(response.data.profile));
                i18n.changeLanguage(response.data.profile.languagecode);
            }).catch((error) => {
                console.log('getProfileBasicDetail', error);
            });
        } catch (error) {
            console.log('getProfile', error);
        }
    };

    const getDefaultSecurity = (token) => {
        try {
            profileService.profileFetchstockGet(token).then((response) => {
                console.log('getDefaultSecurity', response);
                
                if (response.data.error.length > 0) {
                    console.log('getDefaultSecurity', response.data.error);
                    toast.current.show({ severity: 'error', summary: 'Hata', detail: response.data.error, life: 3000 });
                    return;
                }
                
                dispatch(setMainDefaultSecurity(response.data.securityCode));
                navigate("/StockAnalysis/summary/" + response.data.securityCode);
            }).catch((error) => {
                console.log('getDefaultSecurity', error);
                // Fall back to TUPRS if there's an error
                navigate("/StockAnalysis/summary/TUPRS");
            });
        } catch (error) {
            console.log('getDefaultSecurity', error);
            // Fall back to TUPRS if there's an error
            navigate("/StockAnalysis/summary/TUPRS");
        }
    };

    const handleVerificationCodeChange = (event) => {
        const nextValue = event.target.value.replace(/\D/g, "").slice(0, 6);
        setVerificationCode(nextValue);
    };

    const userLoginHandler = (event) => {
        event?.preventDefault();

        // Validation
        if (!verificationCode || verificationCode.trim() === "") {
            toast.current.show({
                severity: "error",
                summary: "Doğrulama Hatası",
                detail: "Lütfen doğrulama kodunu giriniz",
                life: 3000
            });
            return;
        }

        if (verificationCode.length !== 6 || !/^\d+$/.test(verificationCode)) {
            toast.current.show({
                severity: "error",
                summary: "Doğrulama Hatası",
                detail: "Doğrulama kodu 6 haneli rakamlardan oluşmalıdır",
                life: 3000
            });
            return;
        }

        // Prevent multiple submissions
        if (isLoading) return;

        setIsLoading(true);

        try {
            eSubeApi.apiEsubeCompleteLoginForceSmsPost(verificationCode, contextVal?.authState.userLoginInputs.mfaTraceId)
                .then((res) => {
                    if (res.data) {
                        if (res.data.Status === "Ok") {
                            // Check if verification was successful
                            if (res.data.Data) {
                                // Use actual response data for login
                                const userData = {
                                    customerNumber: contextVal?.authState.customerNumber,
                                    user: res.data.Data,
                                    token: res.data.Data.TokenId,
                                    accountId: res.data.Data.AccountId,
                                    expireDate: res.data.Data.ExpireDate,
                                    mfaTraceId: contextVal?.authState.userLoginInputs.mfaTraceId,
                                };
                                
                                // Dispatch login with real user data
                                dispatch(login(userData));
                                
                                // Set default security
                                dispatch(setSelectedSecurity({
                                    "alternativeName": "",
                                    "code": "TUPRS",
                                    "Code": "TUPRS",
                                    "countryCode": "TR",
                                    "dataId": 368,
                                    "elite": false,
                                    "id": "sec.101.368",
                                    "index": false,
                                    "listed": true,
                                    "name": "TÜPRAŞ",
                                    "objectId": "sec.101.368",
                                    "screenable": true,
                                    "sourceId": 101,
                                    "uniqueCode": "TUPRS:IS",
                                    "visible": true
                                }));
                                
                                // Fetch user data - same sequence as in MemberLoginForm
                                getUserWatchlistData(res.data.Data.TokenId);
                                getProfileBasicDetail(res.data.Data.TokenId);
                                
                                toast.current.show({
                                    severity: "success",
                                    summary: "Başarılı",
                                    detail: "Giriş başarılı. Yönlendiriliyorsunuz...",
                                    life: 3000
                                });
                                
                                // Navigate to dashboard using default security
                                getDefaultSecurity(res.data.Data.TokenId);
                            } else {
                                toast.current.show({
                                    severity: "error",
                                    summary: "Doğrulama Hatası",
                                    detail: "Doğrulama işlemi başarısız oldu. Lütfen tekrar deneyiniz.",
                                    life: 3000
                                });
                            }
                        } else if (res.data.Status === "Error") {
                            let errorMessage = "Bir hata oluştu.";

                            // Handle specific error codes if needed
                            if (res.data.Error && res.data.Error.includes("INVALID_PIN")) {
                                errorMessage = "Geçersiz doğrulama kodu. Lütfen tekrar deneyiniz.";
                            } else if (res.data.Error && res.data.Error.includes("EXPIRED")) {
                                errorMessage = "Doğrulama kodu süresi dolmuş. Lütfen yeni kod isteyiniz.";
                            } else if (res.data.Error) {
                                errorMessage = res.data.Error;
                            }

                            toast.current.show({
                                severity: "error",
                                summary: "Hata",
                                detail: errorMessage,
                                life: 3000
                            });
                        } else {
                            toast.current.show({
                                severity: "error",
                                summary: "Bilinmeyen Hata",
                                detail: "İşlem sırasında beklenmeyen bir hata oluştu",
                                life: 3000
                            });
                        }
                    } else {
                        toast.current.show({
                            severity: "error",
                            summary: "Sunucu Hatası",
                            detail: "Sunucudan geçersiz yanıt alındı",
                            life: 3000
                        });
                    }
                })
                .catch((error) => {
                    console.error("API error:", error);

                    let errorMessage = "Sunucu ile bağlantı kurulamadı. Lütfen internet bağlantınızı kontrol ediniz.";

                    if (error.response) {
                        // Server responded with an error status code
                        errorMessage = `Sunucu hatası: ${error.response.status}`;
                    } else if (error.request) {
                        // Request was made but no response received
                        errorMessage = "Sunucu yanıt vermiyor. Lütfen daha sonra tekrar deneyiniz.";
                    }

                    toast.current.show({
                        severity: "error",
                        summary: "Bağlantı Hatası",
                        detail: errorMessage,
                        life: 3000
                    });
                })
                .finally(() => {
                    setIsLoading(false);
                });
        } catch (error) {
            console.error("userLoginHandler error:", error);
            setIsLoading(false);

            toast.current.show({
                severity: "error",
                summary: "İşlem Hatası",
                detail: "İşlem sırasında beklenmeyen bir hata oluştu",
                life: 3000
            });
        }
    };

    return (
        <>
            <Toast ref={toast} position="bottom-right" />
            <Container className="verification">
                <Row className="verification-row">
                    <Col md="12">
                        <div className="col-md-12 roboto-bold-34 color-text">SMS ile Doğrulama</div>
                    </Col>
                    <Col md="12" className="input-col">
                        <form className="verification-form" onSubmit={userLoginHandler}>
                            <label className="p-t-10 color-text-grey" htmlFor="sms-verification-code">
                                {SMS_INFO_TEXT}
                            </label>
                            <InputText
                                id="sms-verification-code"
                                className="verification-input"
                                placeholder="6 haneli kod"
                                value={verificationCode}
                                onChange={handleVerificationCodeChange}
                                disabled={isLoading}
                                maxLength={6}
                                inputMode="numeric"
                                pattern="[0-9]*"
                                autoComplete="one-time-code"
                                autoFocus
                            />
                            <div className="verification-timer color-text">
                                Kalan süre {formatTime(seconds)}
                            </div>
                            <button
                                type="button"
                                className="verification-resend-button"
                                onClick={clickResendCode}
                                disabled={seconds > 0 || isLoading}
                            >
                                {isLoading ? (
                                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                                ) : null}
                                Kodu Tekrar Gönder
                            </button>

                            <Row>
                                <Col md="12">
                                    <Button
                                        slot="end"
                                        type="submit"
                                        variant="dark"
                                        className="loginBtn m-t-40"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? (
                                            <FontAwesomeIcon icon={faSpinner} spin />
                                        ) : "DOĞRULA"}
                                    </Button>
                                </Col>
                            </Row>
                        </form>
                    </Col>
                </Row>
            </Container>
        </>
    );
}
